import { useCallback, useEffect, useRef } from 'react';

type Options = {
  isOpen: boolean;
  /** The element to focus instead of the opener. */
  getTarget: () => HTMLElement | null | undefined;
  /** Waits this long before focusing, e.g. for the dialog's close transition. */
  delay?: number;
};

/**
 * A dialog can only return focus to its opener while the opener is on the page. When the dialog
 * closes after its opener was replaced, this focuses `getTarget()` instead.
 *
 * Returns a function that records the opener; call it when opening the dialog.
 */
export default function useRefocusIfOpenerGone({
  isOpen,
  getTarget,
  delay,
}: Options): (opener: HTMLElement | null) => void {
  const openerRef = useRef<HTMLElement | null>(null);
  const getTargetRef = useRef(getTarget);
  getTargetRef.current = getTarget;

  useEffect(() => {
    const opener = openerRef.current;
    if (isOpen || !opener) return undefined;
    openerRef.current = null;
    if (opener.isConnected) return undefined;

    const focusTarget = (): void => getTargetRef.current()?.focus();
    if (delay === undefined) {
      focusTarget();
      return undefined;
    }
    const timeout = setTimeout(focusTarget, delay);
    return () => clearTimeout(timeout);
  }, [isOpen, delay]);

  return useCallback((opener: HTMLElement | null) => {
    openerRef.current = opener;
  }, []);
}
